/**
 * Edit Role Template - Platform admin form for editing role templates
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import { Button, FormInput, FormTextArea, FormMultiSelect, FormSelect, FormActions, Badge, Section, Card } from '../../components';
import { AdminPageLayout } from '../../components';
import { useToast } from '../../contexts/ToastContext';
import apiService from '../../services/api';

const EditRoleTemplate = () => {
  const navigate = useNavigate();
  const { id: templateId } = useParams();
  const { showSuccess, showError } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
    is_staff: true
  });
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadTemplate = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [templateData, permissionsData] = await Promise.all([
        apiService.getRoleTemplate(templateId),
        apiService.getAvailablePermissions()
      ]);

      setFormData({
        name: templateData.name,
        description: templateData.description,
        permissions: Array.isArray(templateData.permissions) ? templateData.permissions : [],
        is_staff: templateData.is_staff
      });
      setAvailablePermissions(permissionsData.permissions || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.description.trim() || formData.permissions.length === 0) {
      showError('Please fill in all required fields and select at least one permission');
      return;
    }

    setIsSubmitting(true);
    try {
      await apiService.updateRoleTemplate(templateId, formData);
      showSuccess('Role template updated successfully');
      navigate(`/admin/role-templates/${templateId}`);
    } catch (err) {
      showError(`Failed to update role template: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AdminPageLayout title="REFINE ROLE ARCHITECTURE" maxWidth="1400px">
        <div style={{ padding: '4rem', textAlign: 'center', opacity: 0.6 }}>Synchronizing governance patterns...</div>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout title="REFINE ROLE ARCHITECTURE" maxWidth="1400px">
        <Section title="Synchronisation Error">
          <Card level={1}>
            <p style={{ color: 'var(--color-error)', marginBottom: '1.5rem' }}>Failed to retrieve pattern: {error}</p>
            <Button onClick={loadTemplate} variant="primary">Attempt Resync</Button>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="REFINE ROLE ARCHITECTURE"
      subtitle={`Modifying ${formData.name}`}
      backPath={`/admin/role-templates/${templateId}`}
      maxWidth="1400px"
    >
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>

          <Section title="Governance Metadata">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <FormInput
                  label="Role Designation *"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Strategic Operational Director"
                  required
                />

                <FormTextArea
                  label="Scope of Authority *"
                  name="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Document the high-level responsibilities and governance scope of this role..."
                  rows={4}
                  required
                />

                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', padding: '1.25rem', background: 'var(--surface-lvl2)', borderRadius: '12px', border: '1px solid var(--color-border-light)' }}>
                  <div style={{ flex: 1 }}>
                    <FormSelect
                      label="Classification Level"
                      name="is_staff"
                      value={formData.is_staff ? 'staff' : 'user'}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_staff: e.target.value === 'staff' }))}
                      options={[
                        { value: 'staff', label: 'Ecosystem Staff / Authorized Entity' },
                        { value: 'user', label: 'Standard Platform Participant' }
                      ]}
                      help="Authorized entities include coaches, directors, and administrators. Participants are typically parents and players."
                    />
                  </div>
                  {formData.is_staff ? (
                    <Badge variant="primary" size="sm">STAFF AUTHORITY</Badge>
                  ) : (
                    <Badge variant="outline" size="sm">STANDARD IDENTITY</Badge>
                  )}
                </div>
              </div>
            </Card>
          </Section>

          <Section title="Privilege Matrix">
            <Card level={1}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <FormMultiSelect
                  label="Authorized Capabilities *"
                  name="permissions"
                  value={formData.permissions}
                  onChange={(e) => setFormData(prev => ({ ...prev, permissions: e.target.value || [] }))}
                  options={availablePermissions.map(perm => ({
                    value: perm.id,
                    label: `${perm.name} — ${perm.description}`,
                    group: perm.category
                  }))}
                  placeholder="Select governance patterns for this role architecture"
                  required
                />
              </div>
            </Card>
          </Section>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'flex-end',
            marginTop: '1rem',
            padding: '2rem',
            background: 'var(--surface-lvl1)',
            borderRadius: '16px',
            border: '1px solid var(--color-border-light)'
          }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/admin/role-templates/${templateId}`)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="save"
              loading={isSubmitting}
              disabled={isSubmitting || !formData.name.trim() || !formData.description.trim() || formData.permissions.length === 0}
            >
              Finalise Refinement
            </Button>
          </div>
        </div>
      </form>
    </AdminPageLayout>
  );
};

export default EditRoleTemplate;